import { filename } from "pathe/utils";
import objectHash from "object-hash";
import { MasterProduct, Product } from "~/types/common";
import { useFilterRegistry } from "~/stores/filterRegistry";

export function useHelpers() {
  const totalStockOfVariant = (variant: Product): number => {
    return variant.product.product_keszlet_property.reduce((total, store) => {
      const stock = store && "value" in store ? store.value : 0;
      // const stock = store && "value" in store && "key" in store && store.key !== "miskolc" ? store.value : 0; // MNTX-688

      return total + stock;
    }, 0);
  };

  const getMasterProductBadge = (
    masterProduct: MasterProduct,
    priceFieldToUse: string,
  ): Record<any, any> => {
    let badge = { type: "none" };

    // check for price discount
    if (masterProduct[priceFieldToUse].in_action) {
      const discount = Math.round(
        (100 *
          (masterProduct[priceFieldToUse].original_price_min -
            masterProduct[priceFieldToUse].price_min)) /
          masterProduct[priceFieldToUse].original_price_min,
      );
      if (discount > 0) {
        badge = {
          type: "discountPercent",
          value: discount,
        };
      }
    }
    return badge;
  };

  const getVariantByProperty = (
    masterProduct: MasterProduct,
    property: string,
    value: any,
  ): Product | undefined => {
    return masterProduct.products.find((variant: Product) => {
      if (property in variant.product) {
        return variant.product[property] === value;
      } else {
        return false;
      }
    });
  };

  const isIconFont = (src: any): boolean => {
    if (typeof src === "string" && src.length > 0) {
      return !isIconImage(src);
    }

    return false;
  };

  const isIconImage = (src: any): boolean => {
    if (typeof src === "string" && src.length > 0) {
      return /\.(svg|png|jpg|jpeg|webp|gif|apng|avif)$/.test(src);
    }

    return false;
  };

  const productPageUrl = (
    product: Record<string, any> | null,
    relative: boolean = true,
  ): string => {
    if (product) {
      let base = "/";
      if (!relative) {
        const url = useRequestURL();
        base = url.protocol + "//" + url.host + "/";
      }
      const config = useRuntimeConfig();
      const name = product.url_code;
      return encodeURI(
        `${base}${config.public.routes.product}/${product.sku}/${name}`,
      );
    }
    return "";
  };

  const useAssets = () => {
    const config = useRuntimeConfig();
    // this is ugly, but import.meta.blog can only take strings, not literals.
    if (config.public.theme.site === "tnf") {
      const glob = import.meta.glob(`@/assets/theme/tnf/*.png`, {
        eager: true,
      });
      const themePngs = Object.fromEntries(
        Object.entries(glob).map(([key, value]) => [
          filename(key),
          value.default,
        ]),
      );
      return { themePngs };
    }
  };

  // the wysiwyg in admin saves "<p><br></p>" for empty content, we don't want to display such non-real content
  const isRealContent = (content: string | null | undefined) => {
    const stripped = content?.replace(/<[^>]*>/g, "").trim();
    if (stripped) return stripped.length > 0;
    return false;
  };

  const createMetaDescription = (htmlString: string | undefined) => {
    if (!htmlString) return "";

    // Step 1: Remove HTML tags using a regex
    const textContent = htmlString.replace(/<[^>]*>/g, "").trim();

    // Step 2: Truncate the string to 150 characters
    const maxLength = 150;
    return textContent.length > maxLength
      ? textContent.substring(0, maxLength) + "..."
      : textContent;
  };

  const getValueByKeyFromKeyValueData = (
    kvData: Record<string, any>[] | undefined | null,
    key: string,
  ) => {
    if (!Array.isArray(kvData)) return null;
    const foundItem = kvData.find(
      (item: Record<string, any>) => item.key === key,
    );
    return foundItem ? foundItem.value : null;
  };

  const logoutUser = async () => {
    const { signOut /*, status: authStatus ,data: authData */ } = useAuth();
    const checkoutStore = useCheckoutStore();
    const cartStore = useCartStore();
    const { $api } = useNuxtApp();

    // console.log("signing out from logoutUser()");

    checkoutStore.reset();
    localStorage.setItem("webshop.cart.code", "");

    await $api("user/frontend-logout").catch((/* err */) => {
      // if the personal access token in API is expired / deleted then we can get 401 for this, so need to catch it to avoid "unhandled" error
      // console.error(err.message);
    });
    cartStore.fetchCartData();
    signOut();
  };

  const bucketBaseUrl = () => {
    const config = useRuntimeConfig();

    if (String(config.public.bucketName).includes(".")) {
      // if it includes a dot then it should be a full domain throught CDN so we use that for shorter URL's, see: https://docs.wasabi.com/docs/how-do-i-use-cloudflare-with-wasabi
      if (String(config.public.bucketName).endsWith("/")) {
        return String(config.public.bucketName);
      }
      return String(config.public.bucketName) + "/";
    } else {
      // otherwise just a normal bucket without a CDN domain
      return `https://s3.eu-central-2.wasabisys.com/${config.public.bucketName}/`;
    }
  };

  const getAssetUrl = (originalUrl: string) => {
    if (!originalUrl.includes("Public/assets")) {
      return encodeURI(originalUrl);
    }

    const relativeUrl = encodeURI(
      originalUrl.replace(/^.*Public\/assets(.*)$/, "$1"),
    );

    return `${bucketBaseUrl()}Public/assets${relativeUrl}`;
  };

  const getYoutubeVideoId = (url?: string | null): string => {
    if (!url) {
      return "";
    }

    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);

    if (match && match[2].length === 11) {
      return match[2];
    } else {
      return "";
    }
  };

  const stringToHash = (string: string) => {
    let hash = 0;
    let i;
    let char;

    if (string.length === 0) return hash;

    for (i = 0; i < string.length; i++) {
      char = string.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }

    return hash;
  };

  const getActiveFilters = (): Record<string, string> => {
    const attributes = useFilterRegistry().attributes;
    const activeFilters = {};
    const route = useRoute();

    for (const name in route.query) {
      if (attributes.includes(name)) {
        activeFilters[name] = route.query[name];
      }
    }

    return activeFilters;
  };

  const hasActiveFilter = () => {
    return Object.keys(getActiveFilters()).length > 0;
  };

  const isIOS = () => {
    if (!import.meta.client) {
      return false;
    }

    return (
      [
        "iPad Simulator",
        "iPhone Simulator",
        "iPod Simulator",
        "iPad",
        "iPhone",
        "iPod",
      ].includes(navigator.platform) ||
      // iPad on iOS 13 detection
      (navigator.userAgent.includes("Mac") && "ontouchend" in document)
    );
  };

  const uuid = () => {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
      /[xy]/g,
      function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      },
    );
  };

  /**
   * @param validParams   Not specifying the parameter means every search param
   *                      is considered, passing empty array means no search
   *                      params are considered
   */
  const getUrlHash = (validParams?: string[]) => {
    const url = new URL(window.location.href);
    const validPresentParams: string[] = [];
    const urlUniqueParts = [
      window.location.protocol,
      window.location.hostname,
      window.location.pathname,
    ];

    // Make sure the params orders won't change hash
    url.searchParams.sort();

    url.searchParams.forEach((value, key) => {
      if (!validParams || validParams.includes(key)) {
        // The query param values have to be decoded before it can be used as they are
        // encoded when the user navigates back to the product list and this results
        // in a different URL hash.
        validPresentParams.push(`${key}=${decodeURIComponent(value)}`);
      }
    });

    urlUniqueParts.push(...validPresentParams);

    return objectHash(urlUniqueParts);
  };

  const getMaximumFractionDigits = () => {
    const config = useRuntimeConfig();
    if (["HUF", "RON"].includes(config.public.currency)) {
      return 0;
    } else if (["EUR"].includes(config.public.currency)) {
      return 2;
    } else {
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.log("Invalid currency: ", config.public.currency);
      }
      return 0;
    }
  };

  function isImageAnimation(url: string) {
    return (
      url.toLowerCase().endsWith(".apng") || url.toLowerCase().endsWith(".gif")
    );
  }

  return {
    bucketBaseUrl,
    getAssetUrl,
    totalStockOfVariant,
    getVariantByProperty,
    isIconFont,
    isIconImage,
    productPageUrl,
    useAssets,
    isImageAnimation,
    isRealContent,
    createMetaDescription,
    getValueByKeyFromKeyValueData,
    logoutUser,
    getYoutubeVideoId,
    stringToHash,
    getActiveFilters,
    hasActiveFilter,
    isIOS,
    uuid,
    getUrlHash,
    getMasterProductBadge,
    getMaximumFractionDigits,
  };
}
